import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isNativeIOS } from "@/lib/nativePush";
import { initIap, getIapProduct, getIapLastError, IAP_PRODUCT_IDS } from "@/lib/iap";

// On-device proof that the app runs inside the native Capacitor shell and
// that StoreKit is reachable. Admin-only diagnostic (hidden from reviewers).
export default function NativeCheckPage() {
  const { user } = useAuth(); // null = auth still checking, false = anonymous
  const [iap, setIap] = useState("checking");
  const [offer, setOffer] = useState(null);
  const [iapError, setIapError] = useState(null);

  useEffect(() => {
    if (!isNativeIOS()) { setIap("n/a"); return; }
    initIap().then((ok) => {
      setIap(ok ? "ready" : "unavailable");
      setIapError(getIapLastError());
      const p = getIapProduct(IAP_PRODUCT_IDS.coins_500);
      setOffer(p?.pricing?.price || null);
    });
  }, []);

  const Row = ({ ok, label, value }) => (
    <div className="flex items-center gap-3 py-3 border-b border-white/10">
      {ok === null ? (
        <Loader2 size={18} className="text-zinc-400 animate-spin" />
      ) : ok ? (
        <CheckCircle2 size={18} className="text-emerald-400" />
      ) : (
        <XCircle size={18} className="text-rose-500" />
      )}
      <span className="text-sm text-zinc-300 flex-1">{label}</span>
      <span className="text-sm text-white font-mono">{value}</span>
    </div>
  );

  return (
    <div
      data-testid="native-check-root"
      className="min-h-screen bg-[#0a0a0a] text-white p-6 max-w-md mx-auto"
      style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}
    >
      {user === null ? null : user && user.role === "admin" ? (
        <NativeCheckBody iap={iap} offer={offer} iapError={iapError} Row={Row} />
      ) : (
        <Navigate to="/feed" replace />
      )}
    </div>
  );
}

function NativeCheckBody({ iap, offer, iapError, Row }) {
  const cap = window.Capacitor;
  const nativeShell = !!cap?.isNativePlatform?.();
  const platform = cap?.getPlatform?.() || "browser";
  const inSafari = !nativeShell && /iphone|ipad/i.test(navigator.userAgent);
  return (
    <>
      <h1 className="font-display text-2xl mb-1">Native Shell Check</h1>
      <p className="text-xs text-zinc-500 mb-6">Diagnostic for Apple IAP readiness.</p>

      <div
        data-testid="native-check-verdict"
        className={`p-4 mb-6 border text-center font-display text-xl ${
          nativeShell ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-rose-600/50 bg-rose-600/10 text-rose-300"
        }`}
      >
        {nativeShell ? "NATIVE APP ✓ — Apple purchases can work here" : inSafari ? "SAFARI / BROWSER — this is NOT the app. Open the myPandaX app icon instead." : "REGULAR BROWSER — web checkout applies"}
      </div>

      <Row ok={nativeShell} label="Capacitor native shell" value={String(nativeShell)} />
      <Row ok={platform === "ios" ? true : nativeShell} label="Platform" value={platform} />
      <Row
        ok={iap === "checking" ? null : iap === "ready"}
        label="StoreKit plugin (CdvPurchase)"
        value={iap}
      />
      <Row ok={!!offer || iap !== "ready"} label="coins_500 Apple price" value={offer || "—"} />
      <Row ok={true} label="coins_500 Product ID" value={IAP_PRODUCT_IDS.coins_500} />
      {iap === "checking" && (
        <div data-testid="native-check-iap-loading" className="mt-4 p-3 border border-white/10 bg-white/[0.03] text-xs text-zinc-400">
          Initializing StoreKit and requesting products from Apple… this can take up to 30 seconds.
        </div>
      )}
      {iapError && (
        <div data-testid="native-check-iap-error" className="mt-4 p-3 border border-rose-600/50 bg-rose-600/10">
          <div className="text-xs uppercase tracking-widest text-rose-400 mb-1">StoreKit error detail</div>
          <div className="text-sm text-rose-200 font-mono break-words">{iapError}</div>
        </div>
      )}
      <Row ok={true} label="Page origin" value={window.location.hostname} />
      <Row ok={true} label="User agent iOS" value={/iphone|ipad/i.test(navigator.userAgent) ? "yes" : "no"} />
    </>
  );
}
